import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Sparkles, Plus, Check, Film, ImageIcon, Loader2, Download } from "lucide-react";

const SAMPLES = [
  { file: "av-ad-1-hero.png", label: "AV Tech — Brand Hero", type: "image" },
  { file: "av-ad-2-benefits.png", label: "AV Tech — Benefits", type: "image" },
  { file: "av-ad-3-produce.png", label: "Produce Promo", type: "image" },
  { file: "av-img-welcome.png", label: "Welcome to Produce at Oakton", type: "image" },
  { file: "av-img-specials.png", label: "Weekly Specials", type: "image" },
  { file: "av-ad-video.mp4", label: "AV Tech — Motion Ad", type: "video" },
  { file: "av-video-produce.mp4", label: "Produce — Motion Ad", type: "video" },
];

export default function SampleAds() {
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState({});
  const [allBusy, setAllBusy] = useState(false);

  useEffect(() => {
    api.get("/media").then((r) => {
      const names = new Set(r.data.map((m) => m.original_filename));
      const d = {};
      SAMPLES.forEach((s) => { if (names.has(s.file)) d[s.file] = true; });
      setDone(d);
    });
  }, []);

  const importOne = async (s) => {
    setBusy((b) => ({ ...b, [s.file]: true }));
    try {
      await api.post("/media/import-sample", { filename: s.file, url: `${window.location.origin}/${s.file}` });
      setDone((d) => ({ ...d, [s.file]: true }));
      toast.success(`"${s.label}" added to your Media Library`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add sample");
    } finally {
      setBusy((b) => ({ ...b, [s.file]: false }));
    }
  };

  const importAll = async () => {
    setAllBusy(true);
    for (const s of SAMPLES) {
      if (!done[s.file]) await importOne(s);
    }
    setAllBusy(false);
    toast.success("All samples added to your library");
  };

  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-primary" /> Sample Ads
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Ready-made ads — add to your library in one click, then drop into a playlist.</p>
        </div>
        <Button onClick={importAll} disabled={allBusy} data-testid="import-all-button">
          {allBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
          Add all to Library
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {SAMPLES.map((s) => (
          <div key={s.file} data-testid={`sample-${s.file}`} className="bg-card border border-border rounded-md overflow-hidden fade-up">
            <div className="relative aspect-video bg-black">
              {s.type === "video" ? (
                <video src={`${window.location.origin}/${s.file}`} className="w-full h-full object-cover" muted preload="metadata" playsInline />
              ) : (
                <img src={`${window.location.origin}/${s.file}`} alt={s.label} className="w-full h-full object-cover" />
              )}
              <span className="absolute top-2 left-2 bg-black/70 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider flex items-center gap-1">
                {s.type === "video" ? <Film className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />} {s.type}
              </span>
            </div>
            <div className="p-3">
              <p className="text-sm font-medium truncate mb-2.5">{s.label}</p>
              <div className="flex gap-2">
                {done[s.file] ? (
                  <Button variant="secondary" size="sm" className="flex-1 text-xs text-green-500" disabled data-testid={`sample-added-${s.file}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> In Library
                  </Button>
                ) : (
                  <Button onClick={() => importOne(s)} disabled={busy[s.file]} size="sm" className="flex-1 text-xs" data-testid={`sample-add-${s.file}`}>
                    {busy[s.file] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
                    Add to Library
                  </Button>
                )}
                <a href={`${window.location.origin}/${s.file}`} download className="inline-flex">
                  <Button variant="secondary" size="sm" className="text-xs px-2.5" title="Download">
                    <Download className="w-3.5 h-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
